import React, { useRef, useState, useCallback, useEffect, useMemo } from "react";
import {
  HiOutlineArrowLeft,
  HiOutlineSparkles,
  HiOutlineExclamationTriangle,
  HiOutlinePlay,
  HiOutlineArrowUturnLeft,
  HiOutlineBriefcase,
  HiOutlineMapPin,
  HiOutlineCurrencyDollar,
  HiOutlineClock,
  HiOutlineCog6Tooth,
  HiOutlineCheckBadge,
  HiOutlineUserCircle,
  HiOutlineStar,
  HiOutlineDocumentText,
  HiOutlineBolt,
} from "react-icons/hi2";
import { Tooltip } from "antd";
import { FormInput, FormButton } from "../../ui";
import { useUser } from "../../hooks";
import { promptsApi } from "../../api";
import { PROMPT_VARIABLE_GROUPS } from "./prompt-variables";
import { notify } from "../../util/notify/notify";
import "./ai-prompts.scss";

export interface IEditorVariable {
  label: string;
  value: string;
  content?: string;
}

interface PromptEditorProps {
  isOpen: boolean;
  title: string;
  name: string;
  prompt: string;
  saving: boolean;
  loading?: boolean;
  saveLabel?: string;
  variables?: IEditorVariable[];
  onNameChange: (value: string) => void;
  onPromptChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

interface IResolvedVar {
  variable: string;
  value: string;
}

interface IPreviewJob {
  title: string;
  description: string;
  budget: string;
  location: string;
  duration: string;
  skills: string[];
  payment_type: string;
  experience_level: string;
  category: string;
  workload: string;
  proposals: string;
  connects: string;
  is_premium: string;
  client: {
    name: string;
    country: string;
    city: string;
    payment_verified: string;
    total_spent: string;
    hire_rate: string;
    rating: string;
    member_since: string;
    total_reviews: string;
    jobs_posted: string;
    jobs_open: string;
  };
}

interface IPreviewData {
  job: IPreviewJob;
  coverLetter: string;
  resolvedVars: IResolvedVar[];
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function highlightBrackets(text: string): string {
  const escaped = escapeHtml(text);
  return (
    escaped.replace(
      /\[([^\]]+)\]/g,
      '<mark class="prompt-highlight">[$1]</mark>'
    ) + "\n"
  );
}


export const PromptEditor = ({
  isOpen,
  title,
  name,
  prompt,
  saving,
  loading = false,
  saveLabel = "Save Prompt",
  variables = [],
  onNameChange,
  onPromptChange,
  onSave,
  onCancel,
}: PromptEditorProps): JSX.Element => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const [nameError, setNameError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewStep, setPreviewStep] = useState(0);
  const [previewData, setPreviewData] = useState<IPreviewData | null>(null);
  const [reuseJob, setReuseJob] = useState(false);
  const [cachedJobData, setCachedJobData] = useState<Record<string, unknown> | null>(null);
  const [improveChanges, setImproveChanges] = useState<string[]>([]);
  const { currentUser } = useUser();

  const profile = currentUser?.upwork_profile || null;
  const hasSyncedProfile = !!profile;
  const portraitUrl = profile?.portrait?.portrait500 || profile?.portrait?.bigPortrait || profile?.portrait?.portrait || null;

  // Real values for Freelancer variable tooltips — undefined = not tracked, "" = tracked but empty
  const liveValues: Record<string, string | undefined> = useMemo(() => {
    if (!profile) return {};
    return {
      "[Freelancer Name]": profile.username || "",
      "[Hourly Rate]": undefined, // comes from job page, not stored in profile
      "[Skills]": (Array.isArray(profile.skills) ? profile.skills : []).join(", ") || "",
      "[Freelancer Country]": profile.country || "",
      "[Freelancer City]": profile.city || "",
      "[Freelancer Timezone]": profile.countryTimezone || "",
    };
  }, [profile]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Reset state when editor closes
  useEffect(() => {
    if (!isOpen) {
      setPreviewing(false);
      setPreviewData(null);
      setReuseJob(false);
      setCachedJobData(null);
      setImproveChanges([]);
    }
  }, [isOpen]);

  const handleScroll = useCallback(() => {
    const ta = textareaRef.current;
    const bd = backdropRef.current;
    if (ta && bd) {
      bd.scrollTop = ta.scrollTop;
      bd.scrollLeft = ta.scrollLeft;
    }
  }, []);

  const insertVariable = (value: string) => {
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newText = prompt.slice(0, start) + value + prompt.slice(end);
    onPromptChange(newText);
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + value.length;
    });
  };

  const handleSave = () => {
    if (!name.trim()) {
      setNameError("Please enter a prompt name");
      return;
    }
    setNameError("");
    onSave();
  };

  const handleCancel = () => {
    setNameError("");
    if (previewing) {
      setPreviewing(false);
      return;
    }
    onCancel();
  };

  const handleImprove = async () => {
    if (!hasSyncedProfile) {
      notify("error", "Sync your Upwork profile first to use AI Improve");
      return;
    }
    setGenerating(true);
    setImproveChanges([]);
    try {
      const res = await promptsApi.improveTemplate(prompt.trim());
      if (res?.prompt) {
        onPromptChange(res.prompt);
        if (res.name && !name.trim()) {
          onNameChange(res.name);
        }
        if (res.changes?.length) {
          setImproveChanges(res.changes);
        }
        notify("success", prompt.trim() ? "Template improved" : "Template generated from your profile");
      }
    } catch (err: any) {
      const msg = err?.text || "Failed to improve template. Try again later.";
      notify("error", msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleTestTemplate = async () => {
    if (!prompt.trim()) {
      notify("error", "Write a template first before testing");
      return;
    }
    const isReusing = reuseJob && cachedJobData;
    if (!isReusing) setPreviewData(null);
    setPreviewLoading(true);
    setPreviewing(true);
    setPreviewStep(isReusing ? 2 : 1);

    // Simulate progress steps while waiting for API
    const stepTimer = setInterval(() => {
      setPreviewStep((s) => (s < 3 ? s + 1 : s));
    }, 4000);

    try {
      const jobToReuse = reuseJob && cachedJobData ? cachedJobData : undefined;
      const res = await promptsApi.testTemplate(prompt, jobToReuse);
      setCachedJobData(res.job_data);
      setPreviewData({
        job: res.job,
        coverLetter: res.cover_letter,
        resolvedVars: res.resolved_vars,
      });
    } catch (err: any) {
      const msg = err?.text || "Failed to generate preview. Try again later.";
      notify("error", msg);
      setPreviewing(false);
    } finally {
      clearInterval(stepTimer);
      setPreviewStep(0);
      setPreviewLoading(false);
    }
  };

  const handleBackToEditor = () => {
    setPreviewing(false);
    setPreviewData(null);
  };

  if (!isOpen) return <></>;

  const hasCustomVars = variables.length > 0;

  return (
    <div className="prompt-editor-fullscreen">
      <div className="prompt-editor-fullscreen__header">
        <div className="prompt-editor-fullscreen__header-left">
        <button className="prompt-editor-fullscreen__back" onClick={handleCancel} type="button">
          <HiOutlineArrowLeft size={18} />
        </button>
        <h2 className="prompt-editor-fullscreen__title">
          {previewing ? "Template Preview" : title}
        </h2>

        <span className="prompt-editor-fullscreen__header-sep prompt-editor-fullscreen__header-sep--full" />

        <div className="prompt-editor-fullscreen__profile">
          {hasSyncedProfile && profile ? (
            <Tooltip title={`${profile.profileTitle || "Upwork Profile"}\n${(Array.isArray(profile.skills) ? profile.skills : []).slice(0, 5).join(", ")}`}>
              <div className="prompt-editor-fullscreen__profile-badge">
                {portraitUrl ? (
                  <img className="prompt-editor-fullscreen__profile-avatar" src={portraitUrl} alt={profile.username} />
                ) : (
                  <div className="prompt-editor-fullscreen__profile-avatar prompt-editor-fullscreen__profile-avatar--placeholder">
                    {(profile.username || "U")[0].toUpperCase()}
                  </div>
                )}
                <span className="prompt-editor-fullscreen__profile-name">{profile.username}</span>
                <span className="prompt-editor-fullscreen__profile-dot" />
              </div>
            </Tooltip>
          ) : (
            <div className="prompt-editor-fullscreen__profile-badge prompt-editor-fullscreen__profile-badge--warning">
              <HiOutlineExclamationTriangle size={14} />
              <span className="prompt-editor-fullscreen__profile-name">No profile synced</span>
            </div>
          )}
        </div>
        </div>

        <div className="prompt-editor-fullscreen__header-actions">
          {!previewing && (
            <>
              <div className="prompt-editor-fullscreen__ai-combo">
                {improveChanges.length > 0 && (
                  <Tooltip
                    title={
                      <div className="prompt-editor__chip-tooltip">
                        <div className="prompt-editor__chip-tooltip-desc">Improvements made:</div>
                        <div className="prompt-editor__chip-tooltip-example">
                          {improveChanges.map((c, i) => <div key={i} style={{ marginTop: 4 }}>{i + 1}. {c}</div>)}
                        </div>
                      </div>
                    }
                    placement="bottom"
                    overlayClassName="prompt-chip-tooltip"
                  >
                    <div className="prompt-editor-fullscreen__ai-combo-changes">
                      <HiOutlineSparkles size={13} />
                      <span>{improveChanges.length} changes</span>
                    </div>
                  </Tooltip>
                )}
                <button
                  className="prompt-editor-fullscreen__ai-combo-btn"
                  onClick={handleImprove}
                  disabled={!hasSyncedProfile || generating}
                  type="button"
                >
                  <HiOutlineSparkles size={15} />
                  {generating
                    ? (<>{prompt.trim() ? "Improving" : "Generating"}<span className="prompt-editor-fullscreen__dots"><span>.</span><span>.</span><span>.</span></span></>)
                    : prompt.trim() ? "AI Improve" : "AI Generate"
                  }
                </button>
              </div>
              <span className="prompt-editor-fullscreen__header-sep prompt-editor-fullscreen__header-sep--full" />
              <FormButton theme="grey" onClick={handleCancel}>Cancel</FormButton>
              <FormButton theme="green-light" onClick={handleSave} loading={saving}>{saveLabel}</FormButton>
            </>
          )}
          {previewing && (
            <>
              <FormButton theme="grey" onClick={handleBackToEditor}>
                <HiOutlineArrowUturnLeft size={15} />
                Back to Editor
              </FormButton>
              <FormButton theme="green-light" onClick={handleSave} loading={saving}>{saveLabel}</FormButton>
            </>
          )}
        </div>
      </div>

      <div className="prompt-editor-fullscreen__body">
        <div className={`prompt-editor-fullscreen__sidebar${previewing ? " prompt-editor-fullscreen__sidebar--preview" : ""}`}>
          <div className="prompt-editor-fullscreen__sidebar-scroll">
          {loading ? (
            <>
              <div className="prompt-editor__shimmer-group">
                <div className="prompt-editor__shimmer prompt-editor__shimmer--label" />
                <div className="prompt-editor__shimmer prompt-editor__shimmer--input" />
              </div>
              <div className="prompt-editor__shimmer-group">
                <div className="prompt-editor__shimmer prompt-editor__shimmer--label" />
                <div className="prompt-editor__shimmer-chips">
                  {[70, 90, 60, 80, 55, 75].map((w, i) => (
                    <div key={i} className="prompt-editor__shimmer prompt-editor__shimmer--chip" style={{ width: w }} />
                  ))}
                </div>
              </div>
              <div className="prompt-editor__shimmer-group">
                <div className="prompt-editor__shimmer prompt-editor__shimmer--label" />
                <div className="prompt-editor__shimmer-chips">
                  {[85, 65, 95, 70].map((w, i) => (
                    <div key={i} className="prompt-editor__shimmer prompt-editor__shimmer--chip" style={{ width: w }} />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <FormInput
                label="Template Name"
                name="promptName"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                error={nameError}
                placeholder="e.g. Friendly Cover Letter"
                disabled={previewing}
              />

              {!previewing && (
                <div className="prompt-editor__variables">
                  <span className="prompt-editor__variables-label">Insert variable</span>

                  {hasCustomVars && (
                    <div className="prompt-editor__var-group">
                      <span className="prompt-editor__var-group-title">Template Data</span>
                      <div className="prompt-editor__chips">
                        {variables.map((v) => (
                          <Tooltip
                            key={v.value}
                            title={
                              <div className="prompt-editor__chip-tooltip">
                                <div className="prompt-editor__chip-tooltip-desc">Inserts {v.value} into your template</div>
                                {v.content ? (
                                  <div className="prompt-editor__chip-tooltip-example">
                                    <span>Current value:</span> {v.content.length > 120 ? v.content.slice(0, 120) + "..." : v.content}
                                  </div>
                                ) : (
                                  <div className="prompt-editor__chip-tooltip-example">
                                    <span>Not set</span> - add content in Template Data below
                                  </div>
                                )}
                              </div>
                            }
                            placement="right"
                            overlayClassName="prompt-chip-tooltip"
                          >
                            <button
                              className={`prompt-editor__chip${!v.content ? " prompt-editor__chip--empty" : ""}`}
                              onClick={() => insertVariable(v.value)}
                              type="button"
                            >
                              {v.label}
                            </button>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  )}

                  {PROMPT_VARIABLE_GROUPS.map((g) => (
                    <div key={g.group} className="prompt-editor__var-group">
                      <span className="prompt-editor__var-group-title">{g.group}</span>
                      <div className="prompt-editor__chips">
                        {g.variables.map((v) => {
                          const live = liveValues[v.value];
                          const isFreelancerVar = g.group === "Freelancer";
                          const needsProfile = isFreelancerVar && !hasSyncedProfile;
                          return (
                          <Tooltip
                            key={v.value}
                            title={
                              <div className="prompt-editor__chip-tooltip">
                                {needsProfile ? (
                                  <>
                                    <div className="prompt-editor__chip-tooltip-desc">Sync your Upwork profile to use this variable</div>
                                    <div className="prompt-editor__chip-tooltip-example">
                                      <span>Example:</span> {v.example}
                                    </div>
                                  </>
                                ) : live ? (
                                  <>
                                    <div className="prompt-editor__chip-tooltip-desc">{v.description}</div>
                                    <div className="prompt-editor__chip-tooltip-example">
                                      <span>Your value:</span> {live}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="prompt-editor__chip-tooltip-desc">{v.description}</div>
                                    <div className="prompt-editor__chip-tooltip-example">
                                      <span>{v.example2 ? "Example 1:" : "Example:"}</span> {v.example}
                                    </div>
                                    {v.example2 && (
                                      <div className="prompt-editor__chip-tooltip-example">
                                        <span>Example 2:</span> {v.example2}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            }
                            placement="right"
                            overlayClassName="prompt-chip-tooltip"
                          >
                            <button
                              className={`prompt-editor__chip${needsProfile ? " prompt-editor__chip--empty" : ""}`}
                              onClick={() => insertVariable(v.value)}
                              type="button"
                            >
                              {v.label}
                            </button>
                          </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Preview sidebar info ── */}
              {previewing && previewData && !previewLoading && (() => {
                const trimmed = previewData.coverLetter.trim();
                const words = trimmed ? trimmed.split(/\s+/).length : 0;
                const paragraphs = trimmed ? trimmed.split(/\n\s*\n/).filter((p) => p.trim()).length : 0;
                const readingTime = Math.max(1, Math.round(words / 200));

                return (
                  <>
                    <div className="prompt-editor__preview-stats">
                      <span className="prompt-editor__preview-stats-title">Stats</span>
                      <div className="prompt-editor__preview-stats-grid">
                        <div className="prompt-editor__preview-stat">
                          <span className="prompt-editor__preview-stat-value">{words}</span>
                          <span className="prompt-editor__preview-stat-label">Words</span>
                        </div>
                        <div className="prompt-editor__preview-stat">
                          <span className="prompt-editor__preview-stat-value">{paragraphs}</span>
                          <span className="prompt-editor__preview-stat-label">Paragraphs</span>
                        </div>
                        <div className="prompt-editor__preview-stat">
                          <span className="prompt-editor__preview-stat-value">~{readingTime}m</span>
                          <span className="prompt-editor__preview-stat-label">Read time</span>
                        </div>
                      </div>
                    </div>

                    {previewData.resolvedVars.length > 0 && (
                      <div className="prompt-editor__preview-vars">
                        <span className="prompt-editor__preview-stats-title">
                          Resolved Variables ({previewData.resolvedVars.length})
                        </span>
                        <div className="prompt-editor__preview-vars-list">
                          {previewData.resolvedVars.map((rv, i) => (
                            <div key={i} className="prompt-editor__preview-var-item">
                              <span className="prompt-editor__preview-var-key">{rv.variable}</span>
                              <span className="prompt-editor__preview-var-value">{rv.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Preview sidebar shimmer ── */}
              {previewing && previewLoading && (
                <div className="prompt-editor__preview-sidebar-loading">
                  <div className="prompt-editor__shimmer prompt-editor__shimmer--label" />
                  <div className="prompt-editor__preview-stats-grid">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="prompt-editor__shimmer" style={{ height: 52, borderRadius: 8 }} />
                    ))}
                  </div>
                  <div className="prompt-editor__shimmer prompt-editor__shimmer--label" style={{ marginTop: 12 }} />
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="prompt-editor__shimmer" style={{ height: 44, borderRadius: 6 }} />
                  ))}
                </div>
              )}

            </>
          )}
          </div>

          {/* ── Bottom: fixed at sidebar bottom ── */}
          {!loading && (
            <div className="prompt-editor__test-bottom">
              {previewing && cachedJobData && (
                <label className="prompt-editor__reuse-job">
                  <input
                    type="checkbox"
                    checked={reuseJob}
                    onChange={(e) => setReuseJob(e.target.checked)}
                  />
                  <span>Use same job for next test</span>
                </label>
              )}
              <div className="prompt-editor__test-divider" />
              {!hasSyncedProfile ? (
                <Tooltip
                  title="Sync your Upwork profile to test templates"
                  placement="top"
                  overlayClassName="prompt-chip-tooltip"
                >
                  <span className="prompt-editor__test-btn-wrap">
                    <button
                      className="prompt-editor__test-btn prompt-editor__test-btn--disabled"
                      type="button"
                      disabled
                    >
                      <HiOutlinePlay size={16} />
                      Test Template
                    </button>
                  </span>
                </Tooltip>
              ) : (
                <button
                  className="prompt-editor__test-btn"
                  onClick={handleTestTemplate}
                  disabled={previewLoading || !prompt.trim()}
                  type="button"
                >
                  <HiOutlinePlay size={16} />
                  {previewLoading ? "Generating..." : previewing ? "Try Again" : "Test Template"}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Editor or Preview ── */}
        {loading ? (
          <div className="prompt-editor-fullscreen__editor">
            <div className="prompt-editor__shimmer prompt-editor__shimmer--textarea" />
          </div>
        ) : !previewing ? (
          <div className="prompt-editor-fullscreen__editor">
            <div className="prompt-editor__editor-container">
              <div
                ref={backdropRef}
                className="prompt-editor__backdrop"
                dangerouslySetInnerHTML={{ __html: highlightBrackets(prompt) }}
              />
              <textarea
                ref={textareaRef}
                className="prompt-editor__textarea"
                value={prompt}
                onChange={(e) => onPromptChange(e.target.value)}
                onScroll={handleScroll}
                placeholder="Write your prompt here... Use [brackets] for dynamic variables"
                spellCheck={false}
              />
            </div>
          </div>
        ) : (
          <div className="prompt-editor-fullscreen__preview">
            {previewLoading && !previewData ? (
              <div className="prompt-editor-fullscreen__preview-loading">
                <div className="prompt-editor__loading-icon">
                  <HiOutlineCog6Tooth size={32} className="prompt-editor__loading-spin" />
                </div>
                <div className="prompt-editor__loading-steps">
                  {[
                    "Finding a matching job for your profile...",
                    "Analyzing job requirements...",
                    "Writing proposal with your template...",
                  ].map((label, i) => (
                    <div
                      key={i}
                      className={`prompt-editor__loading-step${
                        previewStep > i + 1
                          ? " prompt-editor__loading-step--done"
                          : previewStep === i + 1
                          ? " prompt-editor__loading-step--active"
                          : ""
                      }`}
                    >
                      <span className="prompt-editor__loading-step-dot" />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : previewData ? (
              <div className="prompt-editor-fullscreen__preview-content">
                {/* Job Card — Upwork style */}
                <div className="prompt-editor-fullscreen__preview-job">
                  <div className="prompt-editor-fullscreen__preview-job-header">
                    <HiOutlineBriefcase size={16} />
                    <span>Sample Job</span>
                    {previewData.job.is_premium === "Yes" && (
                      <span className="prompt-editor-fullscreen__preview-badge prompt-editor-fullscreen__preview-badge--premium">Premium</span>
                    )}
                  </div>
                  <h3 className="prompt-editor-fullscreen__preview-job-title">
                    {previewData.job.title}
                  </h3>

                  {/* Meta row */}
                  <div className="prompt-editor-fullscreen__preview-job-meta">
                    {previewData.job.budget && (
                      <span><HiOutlineCurrencyDollar size={14} /> {previewData.job.payment_type === "Hourly" ? `${previewData.job.budget}/hr` : previewData.job.budget}</span>
                    )}
                    {previewData.job.payment_type && (
                      <span className="prompt-editor-fullscreen__preview-tag">{previewData.job.payment_type}</span>
                    )}
                    {previewData.job.experience_level && (
                      <span>{previewData.job.experience_level}</span>
                    )}
                    {previewData.job.duration && (
                      <span><HiOutlineClock size={14} /> {previewData.job.duration}</span>
                    )}
                    {previewData.job.proposals && (
                      <span><HiOutlineDocumentText size={14} /> {previewData.job.proposals} proposals</span>
                    )}
                    {previewData.job.connects && (
                      <span><HiOutlineBolt size={14} /> {previewData.job.connects} connects</span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="prompt-editor-fullscreen__preview-job-desc">
                    {previewData.job.description}
                  </p>

                  {/* Skills */}
                  {previewData.job.skills.length > 0 && (
                    <div className="prompt-editor-fullscreen__preview-job-skills">
                      {previewData.job.skills.map((s) => (
                        <span key={s} className="prompt-editor-fullscreen__preview-skill">{s}</span>
                      ))}
                    </div>
                  )}

                  {/* Client info */}
                  {(previewData.job.client.total_spent || previewData.job.client.country) && (
                    <div className="prompt-editor-fullscreen__preview-client">
                      <div className="prompt-editor-fullscreen__preview-client-header">
                        <HiOutlineUserCircle size={14} />
                        <span>About the client</span>
                        {previewData.job.client.payment_verified === "Yes" && (
                          <span className="prompt-editor-fullscreen__preview-badge prompt-editor-fullscreen__preview-badge--verified">
                            <HiOutlineCheckBadge size={12} /> Payment verified
                          </span>
                        )}
                      </div>
                      <div className="prompt-editor-fullscreen__preview-client-stats">
                        {previewData.job.client.country && (
                          <span><HiOutlineMapPin size={13} /> {previewData.job.client.city ? `${previewData.job.client.city}, ` : ""}{previewData.job.client.country}</span>
                        )}
                        {previewData.job.client.total_spent && (
                          <span><HiOutlineCurrencyDollar size={13} /> {previewData.job.client.total_spent} spent</span>
                        )}
                        {previewData.job.client.hire_rate && (
                          <span>{previewData.job.client.hire_rate} hire rate</span>
                        )}
                        {previewData.job.client.rating && (
                          <span><HiOutlineStar size={13} /> {previewData.job.client.rating}</span>
                        )}
                        {previewData.job.client.jobs_posted && (
                          <span>{previewData.job.client.jobs_posted} jobs posted</span>
                        )}
                        {previewData.job.client.total_reviews && (
                          <span>{previewData.job.client.total_reviews} reviews</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Generated Cover Letter */}
                <div className={`prompt-editor-fullscreen__preview-cover${previewLoading ? " prompt-editor-fullscreen__preview-cover--loading" : ""}`}>
                  <div className="prompt-editor-fullscreen__preview-cover-header">
                    <HiOutlineSparkles size={16} />
                    <span>{previewLoading ? "Regenerating Cover Letter..." : "Generated Cover Letter"}</span>
                  </div>
                  {previewLoading ? (
                    <div className="prompt-editor-fullscreen__preview-cover-shimmer">
                      {[100, 95, 80, 100, 60].map((w, i) => (
                        <div key={i} className="prompt-editor__shimmer" style={{ width: `${w}%`, height: 14, borderRadius: 4 }} />
                      ))}
                    </div>
                  ) : (
                    <div className="prompt-editor-fullscreen__preview-cover-text">
                      {previewData.coverLetter}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
